/*
  UsageDrilldownDialog — shared popover-style dialog listing every
  active exercise program that currently references a particular
  category key or block id.

  Used by:
    * SectionCategoriesCard's "N in use" chip
    * BlockLibrary's "N in use" chip

  The caller supplies a ``fetchUrl`` (backend endpoint) + a friendly
  ``title``. Each row is a clickable link to the client's programs
  tab so operators can jump straight from "42 in use" to the actual
  program that would be affected by a rename or delete.
*/
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, ExternalLink, Archive } from "lucide-react";

export default function UsageDrilldownDialog({
  open, onOpenChange, fetchUrl, title,
}) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!open || !fetchUrl) return;
    setLoading(true);
    try {
      const r = await api.get(fetchUrl);
      setData(r.data);
    } catch (e) {
      setData({ error: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, [open, fetchUrl]);

  useEffect(() => { load(); }, [load]);

  const active = data?.active || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="usage-drilldown-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{title}</DialogTitle>
          <DialogDescription className="text-xs">
            Active programs currently referencing this item. Click any row to
            jump straight to that client's programs tab.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="text-sm text-zinc-600 flex items-center gap-2 py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading usage…
          </div>
        ) : data?.error ? (
          <div className="text-sm text-red-700 py-4">{data.error}</div>
        ) : (
          <div className="space-y-1 max-h-[60vh] overflow-y-auto">
            {active.length === 0 ? (
              <div className="text-sm text-zinc-600 py-4 text-center"
                   data-testid="usage-drilldown-empty">
                No active programs use this. Safe to delete.
              </div>
            ) : (
              <ul className="divide-y divide-zinc-100 border border-zinc-200 rounded-md overflow-hidden">
                {active.map((row, i) => (
                  <li key={row.program_id}
                      className="hover:bg-zinc-50"
                      data-testid={`usage-drilldown-row-${i}`}>
                    <Link
                      to={`/clients/${encodeURIComponent(row.client_email || "")}?tab=programs`}
                      onClick={() => onOpenChange(false)}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-600"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="truncate font-medium">{row.client_name}</div>
                        <div className="text-2xs text-zinc-500 truncate">
                          {row.client_email}
                          {row.updated_at ? ` · updated ${row.updated_at.slice(0, 10)}` : ""}
                        </div>
                      </div>
                      <span className="text-2xs uppercase tracking-wider text-zinc-600 border border-zinc-200 rounded px-1.5 py-0.5 bg-zinc-50">
                        {row.status}
                      </span>
                      <ExternalLink className="h-3.5 w-3.5 text-zinc-400" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {(data?.archived_count || 0) > 0 && (
              <div className="text-2xs text-zinc-500 flex items-center gap-1 mt-2"
                   data-testid="usage-drilldown-archived">
                <Archive className="h-3 w-3" />
                Also referenced by {data.archived_count} archived program{data.archived_count === 1 ? "" : "s"} (hidden).
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
